import { createStyles, Divider } from '@mantine/core';
import { motion } from 'framer-motion';
import React, { useRef } from 'react';
import { NavLink } from 'react-router-dom';

import { useTranslation } from '@portals/i18n';
import { DesignType } from '@portals/types';

import { useCommonDesign } from '../../../../hooks/portal-config';
import { LinkBody, SidebarItemProps } from '../SidebarItem';

export function HubsSidebarItem({
  id,
  badgeComponent,
  indicatorComponent,
  badgeColor,
  badgeText,
  icon,
  to,
  hasBorder,
  isSidebarOpen,
}: SidebarItemProps) {
  const design = useCommonDesign();

  const { classes, cx } = useStyles({
    design,
    isSettings: id === 'sidebar.settings',
  });

  const t = useTranslation();
  const label = t(id || '');
  const containerRef = useRef(null);

  return (
    <motion.div
      layout="position"
      ref={containerRef}
      className={cx(classes.container, { open: isSidebarOpen })}
    >
      {hasBorder ? <Divider className={classes.divider} my="lg" /> : null}

      <NavLink
        to={to[0].replace('/*', '')}
        className="sidebar-link"
        data-testid={`sidebar-item-${id}`}
      >
        <LinkBody
          icon={icon}
          label={label}
          badgeColor={badgeColor}
          badgeText={badgeText}
          badgeComponent={badgeComponent}
          indicatorComponent={indicatorComponent}
          isSidebarOpen={isSidebarOpen}
        />
      </NavLink>
    </motion.div>
  );
}

const useStyles = createStyles(
  (
    theme,
    {
      design,
      isSettings,
    }: { design: DesignType | undefined; isSettings: boolean }
  ) => ({
    container: {
      transition: 'padding 0.1s linear',
      marginBottom: '5px',
      position: 'relative',
      padding: 0,

      ...(isSettings
        ? {
            position: 'absolute',
            bottom: theme.spacing.lg,
            width: '100%',
          }
        : {}),

      '.sidebar-link': {
        fontSize: theme.fontSizes.sm,
        fontWeight: 600,
        alignItems: 'center',
        width: '100%',
        padding: '10px 16px',
        borderRadius: theme.radius.md,
        display: 'flex',
        textDecoration: 'none',
        cursor: 'pointer',
        transition:
          'background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, color 0.15s ease-in-out',

        // Branding
        background:
          design?.navigation_menu?.menu_background_color || theme.white,
        color:
          design?.navigation_menu?.menu_text_color || theme.colors.blue_gray[6],

        'i, svg, path': {
          transition: 'stroke 0.15s ease-in-out',
        },

        svg: {
          height: 24,
          width: 24,
        },

        '&:focus': {
          outline: 'none',
        },

        '&:hover': {
          boxShadow: '0 0 1px rgba(0, 0, 0, 0.04)',
          color:
            design?.navigation_menu?.hovered_section_text_color ||
            theme.colors.blue_gray[6],
          background:
            design?.navigation_menu?.hovered_section_background_color ||
            theme.colors.gray[1],
        },

        '&.active': {
          fontWeight: 600,
          backgroundColor:
            design?.navigation_menu?.selected_section_background_color ||
            theme.colors.blue_accent[4],
          color:
            design?.navigation_menu?.selected_section_text_color || theme.white,

          'i, svg, path': {
            stroke:
              design?.navigation_menu?.selected_section_text_color ||
              theme.white,
          },
        },
      },

      '&.open': {
        padding: '0 16px',
      },

      '&:not(.open)': {
        '.sidebar-link': {
          height: 44,
          width: 44,
          margin: '5px 0 5px 16px',
          padding: '12px 10px 10px',
        },
      },
    },
    divider: {
      borderColor: theme.colors.blue_gray[0],
    },
  })
);
